import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

/**
 * Keeps browser "back" inside the app, with the dashboard as the furthest
 * back a logged-in student can go.
 *
 * Browsers limit this on purpose (Chrome's "history manipulation
 * intervention"): history entries a page adds without a fresh user click or
 * key press make the back button skip the *whole app*. Activations stop
 * counting after any back/forward press until the user interacts again. So
 * entries are only ever added right after a click/key press, never in
 * reaction to a back press.
 */

/** Route of the history entry placed beneath the first page of every tab. */
export const HISTORY_FLOOR_PATH = 'start';

const DASHBOARD_PATH = '/dashboard';
/** Depth of a duplicate dashboard entry (1…DASHBOARD_BACK_BUFFER). */
const DASHBOARD_DEPTH_KEY = 'dashboardBackDepth';
/** Back presses absorbed on the dashboard between two interactions. */
const DASHBOARD_BACK_BUFFER = 10;

/**
 * When a tab opens the app fresh (typed URL, new tab, link), a floor entry is
 * slipped in beneath the first page, so "back" from that page lands on the
 * floor (which redirects to the dashboard) instead of the previous site. It
 * is seeded once per tab; it holds once the student has clicked or typed.
 *
 * Runs as an app initializer, before the router's initial navigation stamps
 * its own state on the current entry.
 */
export function seedHistoryFloor(): void {
  const location = inject(Location);
  if (isFloorSeeded() || location.path() === `/${HISTORY_FLOOR_PATH}`) {
    return;
  }

  // Only the tab's first app entry (fresh load, or a reload of page 0) can
  // get a floor beneath it; deeper entries already have app pages below.
  const state = history.state as { ɵrouterPageId?: number } | null;
  const pageId = state?.ɵrouterPageId;
  if (pageId !== undefined && pageId !== 0) {
    return;
  }

  const firstPageUrl = window.location.href;
  // Page id -1 sits just below the router's first page (0), so a back press
  // that the exam screen cancels is undone with history.go(+1).
  history.replaceState(
    { navigationId: -1, ɵrouterPageId: -1 },
    '',
    location.prepareExternalUrl(`/${HISTORY_FLOOR_PATH}`),
  );
  history.pushState(state, '', firstPageUrl);
  markFloorSeeded();
}

/**
 * Logged-in pages (outside the exam flow) that get a dashboard stack beneath
 * them, so "back" from them lands on the dashboard.
 */
const STACKED_PAGE_PATHS = ['/test-series', '/test-series/bookmarks', '/test-series/performance'];
/** Sections whose sub-pages are stacked too (e.g. performance/tests/:testId). */
const STACKED_SECTION_PATHS = ['/test-series/bookmarks', '/test-series/performance'];

/**
 * Keeps a stack of duplicate dashboard entries in history so browser "back"
 * keeps landing on the dashboard:
 *
 * - On the dashboard the stack sits above it; each back press uses up one
 *   entry and every click/key press tops it up again.
 * - On test series / bookmarks / performance, the first click or key press
 *   slips the stack in beneath the page, so "back" goes to the dashboard
 *   (even when the tab was opened directly on that page).
 *
 * Exam-flow pages are left alone; `examFlowHistoryGuard` sends back/forward
 * from them to the dashboard.
 *
 * Runs as an app initializer and lives for the whole app.
 */
export function trapDashboardBack(): void {
  const router = inject(Router);
  const location = inject(Location);

  const arm = () => {
    const path = router.url.split(/[?#]/)[0];
    const state = (history.state ?? {}) as Record<string, unknown>;

    if (path === DASHBOARD_PATH) {
      // Same URL and router state (page id), so the router treats every
      // entry as the same page.
      const depth = Number(state[DASHBOARD_DEPTH_KEY] ?? 0);
      for (let next = depth + 1; next <= DASHBOARD_BACK_BUFFER; next++) {
        history.pushState({ ...state, [DASHBOARD_DEPTH_KEY]: next }, '', window.location.href);
      }
      return;
    }

    if (isStackedPage(path) && !state[DASHBOARD_BELOW_KEY]) {
      // Turn the current entry into the bottom of a dashboard stack, then put
      // the page itself back on top — the URL bar ends where it started.
      const pageUrl = window.location.href;
      const dashboardUrl = location.prepareExternalUrl(DASHBOARD_PATH);
      for (let depth = 1; depth <= DASHBOARD_BACK_BUFFER; depth++) {
        const entry = { ...state, [DASHBOARD_DEPTH_KEY]: depth };
        if (depth === 1) {
          history.replaceState(entry, '', dashboardUrl);
        } else {
          history.pushState(entry, '', dashboardUrl);
        }
      }
      history.pushState({ ...state, [DASHBOARD_BELOW_KEY]: true }, '', pageUrl);
    }
  };

  // Arriving on a page from a click (sidebar, login, buttons…). Never after
  // back/forward: Chrome stops honouring earlier activations once one happens.
  let trigger: NavigationStart['navigationTrigger'];
  let previousPath = '';
  router.events.subscribe((event) => {
    if (event instanceof NavigationStart) {
      trigger = event.navigationTrigger;
      return;
    }
    if (!(event instanceof NavigationEnd)) {
      return;
    }

    // Clicked through from the dashboard: the dashboard is already beneath.
    if (trigger !== 'popstate' && previousPath === DASHBOARD_PATH) {
      history.replaceState({ ...history.state, [DASHBOARD_BELOW_KEY]: true }, '');
    }
    previousPath = router.url.split(/[?#]/)[0];

    if (trigger !== 'popstate' && hasFreshActivation()) {
      arm();
    }
  });

  // Any interaction re-arms what back presses used up.
  for (const type of ['pointerdown', 'keydown'] as const) {
    window.addEventListener(type, arm, { capture: true });
  }
}

/** Marks a page entry that already has the dashboard (stack) beneath it. */
const DASHBOARD_BELOW_KEY = 'dashboardBelow';

function isStackedPage(path: string): boolean {
  return (
    STACKED_PAGE_PATHS.includes(path) ||
    STACKED_SECTION_PATHS.some((section) => path.startsWith(`${section}/`))
  );
}

/** A click/key press within the last few seconds (transient activation). */
function hasFreshActivation(): boolean {
  return (navigator as { userActivation?: { isActive: boolean } }).userActivation?.isActive ?? false;
}

/** Per tab: sessionStorage survives reloads but not a new tab. */
const FLOOR_SEEDED_KEY = 'historyFloorSeeded';

function isFloorSeeded(): boolean {
  try {
    return sessionStorage.getItem(FLOOR_SEEDED_KEY) === '1';
  } catch {
    return false;
  }
}

function markFloorSeeded(): void {
  try {
    sessionStorage.setItem(FLOOR_SEEDED_KEY, '1');
  } catch {
    // Storage blocked: the floor may be seeded again on reload, which is harmless.
  }
}
